import { getShopifyRoutesRoot } from "./shopify-cart-url";

/** variantId -> compare-at unit price in cents (only successful lookups) */
const compareAtCentsByVariantId = new Map();
/** variantId -> in-flight lookup promise (dedupe concurrent enrich) */
const compareAtPromiseByVariantId = new Map();

function getCartLineProductHandle(item) {
  const handle = String(item?.handle || "").trim();
  if (handle) return handle.replace(/^\/+|\/+$/g, "");
  const url = String(item?.url || "");
  const match = url.match(/\/products\/([^/?#]+)/i);
  if (!match?.[1]) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function toCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n);
}

function getCachedCompareAtCents(variantId) {
  const id = String(variantId || "");
  if (!id || !compareAtCentsByVariantId.has(id)) return null;
  return compareAtCentsByVariantId.get(id) || 0;
}

/**
 * Resolve unit compare-at (cents) for a cart line.
 * Prefers cart.js fields when present; otherwise loads /products/{handle}.js.
 * Network failures are NOT cached as 0 (so retries still work after aborted requests).
 */
async function resolveVariantCompareAtCents(item) {
  const variantId = String(item?.variant_id || "");
  if (!variantId) return 0;

  const fromCart = toCents(
    item?.compare_at_price ?? item?.compareAtPrice ?? item?._weu_compare_at_price
  );
  if (fromCart > 0) {
    compareAtCentsByVariantId.set(variantId, fromCart);
    return fromCart;
  }

  const cached = getCachedCompareAtCents(variantId);
  if (cached != null) return cached;

  if (compareAtPromiseByVariantId.has(variantId)) {
    return compareAtPromiseByVariantId.get(variantId);
  }

  const handle = getCartLineProductHandle(item);
  if (!handle) {
    // No handle to look up — cache as none so we don't keep retrying forever
    compareAtCentsByVariantId.set(variantId, 0);
    return 0;
  }

  const promise = (async () => {
    try {
      const response = await fetch(
        `${getShopifyRoutesRoot()}products/${encodeURIComponent(handle)}.js`
      );
      if (!response.ok) {
        // Transient failure — do not cache
        return 0;
      }
      const product = await response.json();
      const variant = (product?.variants || []).find(
        (entry) => String(entry?.id) === variantId
      );
      const unit = toCents(
        variant?.compare_at_price ?? product?.compare_at_price ?? 0
      );
      compareAtCentsByVariantId.set(variantId, unit);
      return unit;
    } catch {
      // Abort / network error — do not cache 0
      return 0;
    } finally {
      compareAtPromiseByVariantId.delete(variantId);
    }
  })();

  compareAtPromiseByVariantId.set(variantId, promise);
  return promise;
}

function withCompareAtFields(item, unitCents) {
  const unit = toCents(unitCents);
  const qty = Math.max(1, Number(item?.quantity) || 1);
  const line = unit > 0 ? unit * qty : 0;
  if (
    Number(item?._weu_compare_at_price) === unit &&
    Number(item?._weu_compare_at_line) === line
  ) {
    return item;
  }
  return {
    ...item,
    _weu_compare_at_price: unit,
    _weu_compare_at_line: line,
  };
}

/**
 * Sync-apply known compare-at values from memory cache onto a cart snapshot.
 * Safe to call on every cart update before async enrich finishes.
 */
export function applyCachedCompareAtPrices(cart) {
  if (!cart || typeof cart !== "object") return cart;
  const items = Array.isArray(cart.items) ? cart.items : [];
  if (!items.length) return cart;

  let changed = false;
  const nextItems = items.map((item) => {
    const variantId = String(item?.variant_id || "");
    const fromItem = toCents(
      item?.compare_at_price ?? item?.compareAtPrice ?? item?._weu_compare_at_price
    );
    const cached = variantId ? getCachedCompareAtCents(variantId) : null;
    const unit = fromItem > 0 ? fromItem : cached != null ? cached : 0;
    if (unit <= 0 && !(item?._weu_compare_at_price > 0)) return item;
    const next = withCompareAtFields(item, unit > 0 ? unit : item._weu_compare_at_price);
    if (next !== item) changed = true;
    return next;
  });

  if (!changed) return cart;
  return { ...cart, items: nextItems };
}

/**
 * Patch compare-at fields from an enriched cart onto the latest cart snapshot
 * (matched by line key / variant id) so concurrent cart updates don't drop prices.
 */
export function mergeCompareAtOntoCart(targetCart, enrichedCart) {
  if (!targetCart || typeof targetCart !== "object") return targetCart;
  if (!enrichedCart || typeof enrichedCart !== "object") return targetCart;

  const enrichedByKey = new Map();
  const enrichedByVariant = new Map();
  for (const item of enrichedCart.items || []) {
    const key = String(item?.key || "");
    const variantId = String(item?.variant_id || "");
    if (key) enrichedByKey.set(key, item);
    if (variantId && item?._weu_compare_at_price > 0) {
      enrichedByVariant.set(variantId, item);
    }
  }

  let changed = false;
  const nextItems = (targetCart.items || []).map((item) => {
    const key = String(item?.key || "");
    const variantId = String(item?.variant_id || "");
    const source =
      (key && enrichedByKey.get(key)) ||
      (variantId && enrichedByVariant.get(variantId)) ||
      null;
    if (!source || !(source._weu_compare_at_price > 0)) return item;
    const next = withCompareAtFields(item, source._weu_compare_at_price);
    if (next !== item) changed = true;
    return next;
  });

  if (!changed) return targetCart;
  return { ...targetCart, items: nextItems };
}

/**
 * Attach `_weu_compare_at_price` / `_weu_compare_at_line` (cents) onto cart items
 * so line pricing can show catalog compare-at even when cart.js omits it.
 */
export async function enrichCartWithCompareAtPrices(cart) {
  if (!cart || typeof cart !== "object") return cart;
  const items = Array.isArray(cart.items) ? cart.items : [];
  if (!items.length) return cart;

  const unique = [];
  const seen = new Set();
  for (const item of items) {
    const variantId = String(item?.variant_id || "");
    if (!variantId || seen.has(variantId)) continue;
    seen.add(variantId);
    unique.push(item);
  }

  await Promise.all(unique.map((item) => resolveVariantCompareAtCents(item)));

  let changed = false;
  const nextItems = items.map((item) => {
    const variantId = String(item?.variant_id || "");
    const unit =
      toCents(item?.compare_at_price ?? item?.compareAtPrice) ||
      (variantId ? getCachedCompareAtCents(variantId) || 0 : 0) ||
      toCents(item?._weu_compare_at_price);
    const next = withCompareAtFields(item, unit);
    if (next !== item) changed = true;
    return next;
  });

  if (!changed) return cart;
  return { ...cart, items: nextItems };
}
